import { Permission, Permissions } from '@/app/types';
import { errorCodeOf, errorMessageOf, errorNameOf, isCaptureRefusal } from '@/app/utils/errors';
import { CAPTURE_PERMISSION, readCapturePermissionState } from '@/app/utils/permissions';
import { EVENTS, gtag } from '@/lib/gtag';
import SentryImportPromise from '@/sentry/importPromise';

export type CaptureTrigger = 'mount' | 'chip' | 'join' | 'retry';
export type CaptureOutcome = 'granted' | 'refused' | 'joined-without';
export type DeviceOperation = 'toggle' | 'revive' | 'change-device';

/**
 * A refusal is the customer's answer, not a defect: every outcome is counted
 * in analytics and stays in Sentry only as a breadcrumb under a later error.
 */
export async function reportCaptureOutcome(
  trigger: CaptureTrigger,
  outcome: CaptureOutcome,
  requested: Permissions,
  error?: unknown,
) {
  const [microphonePermission, cameraPermission] = await Promise.all([
    readCapturePermissionState('microphone'),
    readCapturePermissionState('camera'),
  ]);
  const data = {
    operation: 'lobby-capture',
    trigger,
    outcome,
    audio: Boolean(requested.audio),
    video: Boolean(requested.video),
    microphonePermission,
    cameraPermission,
    errorName: errorNameOf(error),
    errorMessage: errorMessageOf(error),
  };

  // A Sentry chunk that never loaded must not lose the measurement with it.
  try {
    const { default: Sentry } = await SentryImportPromise;

    Sentry.addBreadcrumb({
      category: 'lobby-capture',
      level: 'info',
      message: `Lobby capture ${outcome} on ${trigger}`,
      data,
    });
  } finally {
    gtag(EVENTS.DEVICE_OUTCOME, data);
  }
}

/**
 * Warning, not error: the call goes on, so the level asks for a human to look
 * rather than reporting a crash. A refusal is counted rather than filed, as at
 * the lobby: it is the customer's answer, not our defect. A device the customer
 * has unplugged is neither, and is the caller's to keep out.
 */
export async function reportDeviceFailure(device: Permission, operation: DeviceOperation, error: unknown) {
  const permissionState = await readCapturePermissionState(CAPTURE_PERMISSION[device]);
  const code = errorCodeOf(error) ?? errorNameOf(error) ?? 'UNKNOWN';
  const isRefused = isCaptureRefusal(error);
  const tags = { operation: 'call-device', device, deviceOperation: operation };
  const context = {
    rtcCode: errorCodeOf(error),
    rtcData: (error as { data?: unknown } | null)?.data,
    permissionState,
  };
  const measurement = {
    ...tags,
    outcome: 'refused',
    rtcCode: context.rtcCode,
    permissionState,
  };

  try {
    const { default: Sentry } = await SentryImportPromise;

    if (isRefused) {
      Sentry.addBreadcrumb({
        category: 'call-device',
        level: 'info',
        message: `Capture refused mid-call on ${operation}`,
        data: context,
      });
      return;
    }
    Sentry.captureException(error, {
      level: 'warning',
      tags,
      fingerprint: ['call-device', device, operation, code],
      extra: context,
    });
  } finally {
    if (isRefused) {
      gtag(EVENTS.DEVICE_OUTCOME, measurement);
    }
  }
}

/**
 * A device the customer unplugged is answered in the call rather than filed —
 * but the SDK's console copy went with it, so the rate has to land somewhere.
 */
export function countDeviceAbsent(device: Permission, operation: DeviceOperation) {
  gtag(EVENTS.DEVICE_OUTCOME, { operation: 'call-device', device, deviceOperation: operation, outcome: 'absent' });
}
